// The Desk (correspondence C2) — every thread you follow, in Open / Waiting /
// Resolved lanes. A row opens its record and the dock's Correspondence tab.
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { useSnackbar } from "notistack";
import { brainTokens } from "../../design/brainTheme";
import { PageHeader, SectionCard, Segmented, EmptyState, Icon } from "../../mui";
import { relDate } from "../../mui/format";
import { useCorrespondence } from "../../correspondence/CorrespondenceProvider";
import {
  getDeskApi,
  setDeskMuteApi,
  patchMessageApi,
  type DeskData,
  type DeskLane,
  type DeskRow,
} from "../../lib/brain-api";

const c = brainTokens.color;

const LANES: { key: DeskLane; label: string; tone: string }[] = [
  { key: "open", label: "Open", tone: c.successText2 },
  { key: "waiting", label: "Waiting", tone: c.warnDot },
  { key: "resolved", label: "Resolved", tone: c.faint },
];

type Filter = "all" | "unread" | "mine";

export function DeskPageMui() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { openCorrespondence } = useCorrespondence();
  const [data, setData] = useState<DeskData | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Filter>("all");

  const load = useCallback(async () => {
    try {
      setData(await getDeskApi());
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Failed to load the desk", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => { void load(); }, [load]);

  const keep = (r: DeskRow) =>
    filter === "all" ? true : filter === "unread" ? r.unread : r.waitingOnMe;

  const openRow = (r: DeskRow) => {
    openCorrespondence();       // ask the dock to open onto Correspondence
    navigate(r.url);            // …for the record we're navigating to
  };

  const mute = async (r: DeskRow) => {
    try {
      await setDeskMuteApi(r.source, r.sourceId, true);
      enqueueSnackbar(`Muted ${r.title}`, { variant: "success" });
      await load();
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Failed to mute", { variant: "error" });
    }
  };

  const resolve = async (r: DeskRow) => {
    try {
      await patchMessageApi(r.rootId, { status: "resolved" });
      await load();
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Failed to resolve", { variant: "error" });
    }
  };

  const total = data ? data.lanes.open.length + data.lanes.waiting.length + data.lanes.resolved.length : 0;

  return (
    <Box>
      <PageHeader
        title="Desk"
        actions={
          <Segmented<Filter>
            value={filter}
            onChange={setFilter}
            options={[
              { key: "all", label: "All" },
              { key: "unread", label: "Unread" },
              { key: "mine", label: "Waiting on me" },
            ]}
          />
        }
      />

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
      ) : total === 0 ? (
        <EmptyState icon="messages" title="Nothing on your desk">
          Threads you start, reply to, are mentioned in, or own show up here. Comment on a record to
          begin.
        </EmptyState>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
            gap: 2,
            alignItems: "start",
          }}
        >
          {LANES.map((lane) => {
            const rows = (data?.lanes[lane.key] ?? []).filter(keep);
            return (
              <SectionCard
                key={lane.key}
                label={`${lane.label} · ${rows.length}`}
                labelIcon={
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: lane.tone, flex: "none" }} />
                }
              >
                {rows.length === 0 ? (
                  <Box sx={{ fontSize: 13, color: c.muted2, py: 1 }}>Nothing here.</Box>
                ) : (
                  rows.map((r) => (
                    <Box
                      key={r.rootId}
                      sx={{
                        borderTop: `1px solid ${c.borderLight}`,
                        py: 1.25,
                        "&:first-of-type": { borderTop: "none", pt: 0 },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.25 }}>
                        {r.unread && <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: c.teal, flex: "none" }} />}
                        <Box
                          component="button"
                          onClick={() => openRow(r)}
                          sx={{
                            border: 0, background: "none", p: 0, cursor: "pointer", textAlign: "left",
                            fontSize: 13.5, fontWeight: 600, color: c.ink, flex: 1, minWidth: 0,
                            "&:hover": { color: c.tealInk },
                          }}
                        >
                          {r.title}
                        </Box>
                        {r.waitingOnMe && lane.key === "waiting" && (
                          <Box sx={{ fontSize: 10.5, fontWeight: 700, color: c.warnText3, bgcolor: c.warnBg2, px: 0.75, py: 0.25, borderRadius: "10px" }}>
                            you
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ fontSize: 12.5, color: c.ink4, lineHeight: 1.4, mb: 0.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {r.lastPreview || "—"}
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                        <Box sx={{ fontSize: 11, color: c.faint, fontFamily: brainTokens.font.mono }}>{relDate(r.lastActivityAt)}</Box>
                        <Box sx={{ flex: 1 }} />
                        {lane.key !== "resolved" && (
                          <Box component="button" onClick={() => void resolve(r)} title="Mark resolved"
                            sx={{ border: 0, background: "none", p: 0, cursor: "pointer", fontSize: 11.5, color: c.muted, "&:hover": { color: c.tealInk } }}>
                            Resolve
                          </Box>
                        )}
                        <Box component="button" onClick={() => void mute(r)} title="Mute this record"
                          sx={{ border: 0, background: "none", p: 0, cursor: "pointer", display: "inline-flex", color: c.faint, "&:hover": { color: c.ink3 } }}>
                          <Icon name="bell-off" size={13} />
                        </Box>
                      </Box>
                    </Box>
                  ))
                )}
              </SectionCard>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
